import { useEffect, useState } from "react";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { ImpactBand } from "../components/ImpactBand";
import { WaysToGive } from "../components/WaysToGive";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * /demo/impact-band — preview of the Impact Totals Band before it's mounted
 * on the landing page. Two renders: live figures from /api/impact (sandbox
 * on preview) and a sample payload showing how it reads at scale.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = {
  ready: true, enabled: true, livemode: true,
  grants_disbursed_cents: 4825000, grants_count: 87, students_funded: 61, campaigns_funded: 64,
  first_grant_at: "2026-10-03T18:00:00+00:00", latest_grant_at: "2027-03-27T18:00:00+00:00",
};

const Note = ({ children }) => (
  <p className="mt-3 max-w-2xl text-[13px] italic text-olive">{children}</p>
);

export default function ImpactBandMockup() {
  useNoIndex();
  const [live, setLive] = useState(null);
  useEffect(() => { axios.get(`${API}/impact`).then((r) => setLive(r.data)).catch(() => setLive(null)); }, []);

  return (
    <main data-testid="impact-band-mockup" className="relative min-h-screen bg-sand text-ink">
      <Navbar onStart={() => {}} primaryLabel="Preview the platform" secondaryLabel={null} />

      <section className="mx-auto max-w-6xl px-6 pb-12 pt-32 lg:px-8 lg:pt-44">
        <span className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
          Impact totals band · preview
        </span>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
          Where the money <em className="italic text-tangerine">went.</em>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
          Three figures straight from the grants ledger — dollars disbursed, students funded, grants sent. Numbers count up on scroll. The band stays hidden on the landing page until real money has moved and you flip the switch.
        </p>
        <Note>Not mounted on the landing page yet · not linked from anywhere public.</Note>
        <div className="mt-6 flex flex-wrap gap-2 text-xs" data-testid="impact-status">
          <span className="rounded-full border border-border bg-bone px-3 py-1">ready: <strong>{live ? String(live.ready) : "…"}</strong></span>
          <span className="rounded-full border border-border bg-bone px-3 py-1">enabled (IMPACT_BAND_ENABLED): <strong>{live ? String(live.enabled) : "…"}</strong></span>
          <span className="rounded-full border border-border bg-bone px-3 py-1">mode: <strong>{live ? (live.livemode ? "live" : "sandbox") : "…"}</strong></span>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">A · Live figures from this environment</p>
        <Note>On preview these are sandbox test grants; on production they'd be real. Renders here regardless of the switch.</Note>
      </section>
      <ImpactBand forceShow testid="impact-band-live" />

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-24 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">B · Sample figures at scale</p>
        <Note>How it reads after a couple of semesters — $48,250 across 87 grants to 61 students.</Note>
      </section>
      <ImpactBand sample={SAMPLE} testid="impact-band-sample" />

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-24 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">C · In context — proposed placement after "Ways to give"</p>
        <Note>Dark band breaks the sand rhythm right after the donation ask, before "What your gift covers".</Note>
      </section>
      <WaysToGive onDonate={() => {}} onBrowse={() => {}} />
      <ImpactBand sample={SAMPLE} testid="impact-band-context" />

      <section className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">How it goes live</p>
        <ol className="mt-4 max-w-2xl list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink">
          <li>First real grant clears in production → <code className="rounded bg-bone px-1.5 py-0.5 text-xs">ready</code> flips to true on its own.</li>
          <li>Set <code className="rounded bg-bone px-1.5 py-0.5 text-xs">IMPACT_BAND_ENABLED=true</code> on the backend.</li>
          <li>Mount <code className="rounded bg-bone px-1.5 py-0.5 text-xs">&lt;ImpactBand /&gt;</code> on the landing page (one line) — it stays invisible until both conditions hold.</li>
        </ol>
      </section>
    </main>
  );
}
